import * as React from 'react';

import { Helmet } from 'react-helmet';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import AppAppBar from './components/AppAppBar';
import Hero from './components/Hero';
import Highlights from './components/Highlights';
import Pricing from './components/Pricing';
import Features from './components/Features';
import FAQ, { faqItems } from './components/FAQ';
import CtaBand from './components/CtaBand';
import ScrollFab from './components/ScrollFab';
import Footer from './components/Footer';
import landingTheme from './theme/landingTheme';
import './landing.css';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Marter',
  alternateName: ['Marter Walmart Seller Tool', 'GetMarter'],
  url: 'https://www.getmarter.com/',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Chrome',
  description:
    'Marter is an all-in-one Walmart marketplace seller tool: product research, price history charts, sales estimates, stock alerts and a Chrome extension.',
  offers: {
    '@type': 'Offer',
    price: '24',
    priceCurrency: 'USD',
    priceValidUntil: '2027-12-31',
    url: 'https://www.getmarter.com/pricing',
  },
};

// Brand-entity schema. The bare "marter" query is the site's biggest CTR leak — it was
// spread across 17 of our own URLs at ~2.8% CTR from position ~3.6. Organization +
// WebSite give Google one canonical brand entity to attach the result (and sitelinks) to.
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Marter',
  alternateName: ['GetMarter', 'Marter App', 'Marter Walmart'],
  url: 'https://www.getmarter.com/',
  logo: 'https://www.getmarter.com/logo512.png',
  description:
    'Marter is a Walmart Marketplace seller tool for product research, price history, profit and fee calculation, sales estimates and stock alerts.',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Marter',
  alternateName: 'GetMarter',
  url: 'https://www.getmarter.com/',
  publisher: { '@type': 'Organization', name: 'Marter', url: 'https://www.getmarter.com/' },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

export default function LandingPage(props) {
  return (
    <ThemeProvider theme={landingTheme}>
      {/* Canonical is the bare homepage URL on purpose: affiliate links
          (/?a=...) must consolidate into / instead of ranking separately. */}
      <Helmet>
        <title>Marter | Walmart Seller Tool — Product Research, Price History & Alerts</title>
        <meta
          name="description"
          content="Marter helps Walmart marketplace sellers find profitable products with price history charts, sales estimates, stock alerts and a Chrome extension. One plan, $24/month. Free trial."
        />
        <link rel="canonical" href="https://www.getmarter.com/" />
        <meta property="og:title" content="Marter | Walmart Seller Tool — Product Research, Price History & Alerts" />
        <meta
          property="og:description"
          content="Find profitable products to sell on Walmart with price history, sales estimates and alerts. One plan, $24/month."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getmarter.com/" />
        <meta property="og:site_name" content="Marter - Walmart Seller Tool" />
        <meta property="og:image" content="https://www.getmarter.com/showcase.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <CssBaseline />
      <AppAppBar setMobileAppPopup={props.setMobileAppPopup} setEmailModal={props.setEmailModal} />
      <Hero setEmail={props.setEmail} signUp={props.signUp} silentJim={props.silentJim} />
      <Box sx={{ bgcolor: 'background.default' }}>
        {/* Funnel order: value (features) -> proof (demo) -> offer (pricing)
            -> objections (faq) -> final ask (cta band) */}
        <Features />
        <Highlights />
        <Pricing
          signUp={props.signUp}
          setEmailModal={props.setEmailModal}
          setYearly={props.setYearly}
          silentJim={props.silentJim}
        />
        <FAQ />
        <CtaBand setEmail={props.setEmail} signUp={props.signUp} />
        <Footer />
      </Box>
      <ScrollFab />
    </ThemeProvider>
  );
}
